"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PulsarService } from "@/services/pulsar.service";
import { useAuth } from "./useAuth";

export const MAINNET_PRIVATE_BETA_KEYS = {
  criteria: ["mainnet", "private-beta", "criteria"] as const,
  eligibility: (address?: string) =>
    ["mainnet", "private-beta", "eligibility", address] as const,
};

export function useMainnetPrivateBetaEligibilityCriteria() {
  return useQuery({
    queryKey: MAINNET_PRIVATE_BETA_KEYS.criteria,
    queryFn: () => PulsarService.getMainnetPrivateBetaEligibilityCriteria(),
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * True once the campaign's end date has passed. Driven by the criteria
 * endpoint's `endDate` (available without auth), so the "Completed" UI shows
 * for everyone. Returns false until the criteria loads or if no end date is
 * set. The backend independently enforces no eligibility past this date.
 */
export function useIsPrivateBetaClosed(): boolean {
  const { data } = useMainnetPrivateBetaEligibilityCriteria();
  const endDate = data?.endDate;
  if (!endDate) return false;
  return Date.now() >= new Date(endDate).getTime();
}

export function useMainnetPrivateBetaEligibility() {
  const { address, isAuthenticated, getAccessToken } = useAuth();

  return useQuery({
    queryKey: MAINNET_PRIVATE_BETA_KEYS.eligibility(address),
    queryFn: async () => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error("Authentication is still loading.");
      }

      return PulsarService.getMainnetPrivateBetaEligibility(token);
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    refetchInterval: 30_000,
  });
}

export function useClaimMainnetPrivateBetaDiscordRole() {
  const queryClient = useQueryClient();
  const { address, getAccessToken } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error("Authentication is still loading.");
      }

      return PulsarService.claimMainnetPrivateBetaDiscordRole(token);
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: MAINNET_PRIVATE_BETA_KEYS.eligibility(address),
      });
      toast.success(
        response.alreadyHadRole
          ? `${response.roleName} role already linked`
          : `${response.roleName} role granted`,
      );
    },
    onError: () => {
      toast.error("Could not claim Discord role");
    },
  });
}
